import { usePortalRoutes } from "apps/portal/src/hooks";
import { AuthenticationContext } from "apps/portal/src/providers/authentication/AuthenticationContext";
import { SessionOverlayContext } from "apps/portal/src/providers/authentication/SessionOverlay/SessionOverlayContext";
import { RouteFeature } from "apps/portal/src/routes";
import { Form, FormikHelpers, FormikProvider, useFormik } from "formik";
import QueryString from "qs";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import z from "zod";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";

import { Button, FormInput } from "../../atoms";
import HostedPagesOverviewCard from "../components/HostedPagesOverviewCard";

type FormValues = {
  amount: number | undefined;
};

const presetValues = [20, 50, 100, 150];

const GiftVoucherSchema = z.object({
  amount: z.coerce
    .number({
      required_error: "Amount is required",
      invalid_type_error: "Amount must be a number",
    })
    .int("Amount must be a whole number")
    .min(1, "Amount must be at least 1")
    .optional(),
});

const HostedPagesGiftVoucher = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { createSiteOrEmbedLink } = usePortalRoutes();
  const { userId } = useContext(AuthenticationContext);
  const { setSessionOverlayState } = useContext(SessionOverlayContext);

  const handleCheckout = (
    values: FormValues,
    formikHelpers: FormikHelpers<FormValues>,
  ) => {
    const { setSubmitting } = formikHelpers;
    const numericAmount = values.amount;

    if (!userId) {
      setSessionOverlayState({ screen: "try-sign-in" });
      setSubmitting(false);
      return;
    }
    history.push({
      pathname: createSiteOrEmbedLink(RouteFeature.GiftVoucherCheckout),
      search: QueryString.stringify({
        amount: numericAmount,
      }),
    });
  };

  const formik = useFormik<FormValues>({
    initialValues: { amount: undefined },
    validationSchema: toFormikValidationSchema(GiftVoucherSchema),
    validate: toFormikValidate(GiftVoucherSchema),
    onSubmit: handleCheckout,
  });

  return (
    <div className="flex h-full max-h-full flex-col bg-gray-50 dark:bg-gray-800">
      <div className="track-height bg-gray-0 flex flex-col gap-4 border-b border-gray-300 p-4 lg:px-28 dark:border-gray-700 dark:bg-gray-950">
        <div className="flex flex-row flex-wrap items-center justify-between gap-2">
          <div className="flex flex-row items-center gap-4">
            <div className="dark:text-gray-0 text-xl font-medium text-gray-950">
              {t("page.hostedPagesVoucherCheckout.name")}
            </div>
          </div>
        </div>
      </div>

      <div className="flex w-full grow flex-col items-center">
        <HostedPagesOverviewCard.Container containerClassName="h-full md:h-fit">
          <FormikProvider value={formik}>
            <Form
              className="flex h-full grow flex-col items-center gap-6"
              onSubmit={formik.handleSubmit}
            >
              <HostedPagesOverviewCard.Header>
                <HostedPagesOverviewCard.HeaderTitle>
                  <div className="text-base font-semibold">
                    {t("page.hostedPagesVoucherCheckout.headerTitle")}
                  </div>
                </HostedPagesOverviewCard.HeaderTitle>
                <HostedPagesOverviewCard.HeaderDescription>
                  <div className="mb-5">
                    {t("page.hostedPagesVoucherCheckout.headerDescription")}
                  </div>
                </HostedPagesOverviewCard.HeaderDescription>
              </HostedPagesOverviewCard.Header>

              <div className="flex w-full flex-col gap-4">
                <FormInput
                  type="number"
                  name="amount"
                  label=""
                  placeholder="Enter custom amount"
                />
                <div className="flex flex-row flex-wrap gap-2">
                  {presetValues.map((val) => (
                    <Button
                      key={val}
                      intent={
                        formik.values.amount === val ? "primary" : "default"
                      }
                      className="!rounded-full"
                      type="button"
                      onClick={() => {
                        formik.setFieldValue("amount", val);
                      }}
                    >
                      {val}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="mt-auto flex w-full items-end justify-center md:mt-0">
                <Button
                  className="w-64"
                  intent="secondary"
                  type="submit"
                  disabled={
                    formik.isSubmitting ||
                    !!formik.errors.amount ||
                    !formik.values.amount
                  }
                >
                  {t("page.hostedPagesVoucherCheckout.goToCheckout")}
                </Button>
              </div>
            </Form>
          </FormikProvider>
        </HostedPagesOverviewCard.Container>
      </div>
    </div>
  );
};

export default HostedPagesGiftVoucher;
